// src/helpers/rehypeGroupDocCites.ts

/**
 * Rehype plugin that merges a run of adjacent <doc-cite> elements into one.
 *
 * Models often back one statement with several citations, which would render
 * as a row of chips. A run is <doc-cite> siblings separated only by
 * whitespace; the run is folded into its FIRST element, which gets every ref
 * in order as a space-separated `data-refs` attribute (`data-ref` is left as
 * is). The others and the whitespace between them are removed.
 *
 * Folding into the first element instead of wrapping the run keeps its key
 * (`doc-cite-N`, counted per tag name) stable while a group grows during
 * streaming, so the first chip is never remounted.
 *
 * Only decides adjacency. Which refs resolve, de-duplication and the split by
 * citation type happen at render time (see citationGroups.ts), because
 * payloads can arrive after the markdown is parsed.
 *
 * Must run after rehype-raw: before it, the tags are still raw nodes.
 */

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

const WHITESPACE_ONLY = /^\s*$/;

const isDocCite = (node: HastNode): boolean =>
  node.type === "element" && node.tagName === "doc-cite";

const refOf = (node: HastNode): string => String(node.properties?.dataRef ?? "");

const groupSiblings = (parent: HastNode): void => {
  const children = parent.children;

  if (!children) return;

  for (let i = 0; i < children.length; i++) {
    const node = children[i];

    if (!isDocCite(node)) {
      groupSiblings(node);
      continue;
    }

    // Only this plugin sets data-refs. One the model wrote into a tag itself
    // would pull in refs cited elsewhere in the message.
    if (node.properties) delete node.properties.dataRefs;

    const refs = [refOf(node)];
    let lastInRun = i;

    for (let j = i + 1; j < children.length; j++) {
      const next = children[j];

      if (next.type === "text" && WHITESPACE_ONLY.test(next.value ?? "")) continue;
      if (!isDocCite(next)) break;
      refs.push(refOf(next));
      lastInRun = j;
    }

    if (lastInRun > i) {
      node.properties = { ...node.properties, dataRefs: refs.join(" ") };
      // Whitespace after the last tag in the run stays; it separates the chip
      // from the text that follows.
      children.splice(i + 1, lastInRun - i);
    }
  }
};

export const rehypeGroupDocCites = () => (tree: HastNode) => {
  groupSiblings(tree);
};

export default rehypeGroupDocCites;
